/**
 * Página pública /accept-invitation/:invitationId
 *
 * Ruta pública — el invitado puede NO tener sesión activa. Determina el modo
 * de aceptación con un preview al montar y luego renderiza el formulario
 * correspondiente al modo devuelto por el backend.
 *
 * Modos:
 *   - "signup-required"  → formulario de nombre + contraseña + código
 *   - "login-required"   → campo de código + botón "Enviar enlace de acceso"
 *   - "ready"            → campo de código + botón "Confirmar"
 *   - "wrong-account"    → mensaje de cuenta equivocada + logout
 *   - "invalid"          → mensaje de invitación inválida
 */
import type React from "react";
import { useCallback, useEffect, useReducer } from "react";
import { useNavigate, useParams } from "react-router";
import { signOut } from "../../lib/auth-client";
import {
  InvitationError,
  type InvitationPreviewMode,
  type InvitationPreviewResponse,
  acceptNow,
  acceptSignup,
  loginAndAccept,
  previewInvitation,
} from "../../lib/invitation-api";

// ---------------------------------------------------------------------------
// Estado (reducer pattern para evitar estados inconsistentes)
// ---------------------------------------------------------------------------

type PagePhase =
  | "loading"
  | "signup-required"
  | "login-required"
  | "ready"
  | "wrong-account"
  | "invalid"
  | "magic-link-sent"
  | "accepted";

interface PageState {
  phase: PagePhase;
  orgName: string;
  inviterName: string;
  currentEmail: string;
  acceptCode: string;
  name: string;
  password: string;
  error: string;
  submitting: boolean;
  failedAttempts: number;
}

type PageAction =
  | { type: "PREVIEW_OK"; preview: InvitationPreviewResponse }
  | { type: "PREVIEW_FAIL" }
  | { type: "SET_FIELD"; field: "acceptCode" | "name" | "password"; value: string }
  | { type: "SUBMIT_START" }
  | { type: "SUBMIT_ERROR"; error: string }
  | { type: "SUBMIT_BLOCKED" }
  | { type: "MAGIC_LINK_SENT" }
  | { type: "ACCEPTED" }
  | { type: "CLEAR_ERROR" };

function init(): PageState {
  return {
    phase: "loading",
    orgName: "",
    inviterName: "",
    currentEmail: "",
    acceptCode: "",
    name: "",
    password: "",
    error: "",
    submitting: false,
    failedAttempts: 0,
  };
}

function reducer(state: PageState, action: PageAction): PageState {
  switch (action.type) {
    case "PREVIEW_OK": {
      const mode = action.preview.mode as InvitationPreviewMode;
      return {
        ...state,
        phase: mode,
        orgName: action.preview.orgName ?? "",
        inviterName: action.preview.inviterName ?? "",
        currentEmail: action.preview.currentEmail ?? "",
      };
    }
    case "PREVIEW_FAIL":
      return { ...state, phase: "invalid" };
    case "SET_FIELD":
      return { ...state, [action.field]: action.value, error: "" };
    case "SUBMIT_START":
      return { ...state, submitting: true, error: "" };
    case "SUBMIT_ERROR":
      return {
        ...state,
        submitting: false,
        error: action.error,
        failedAttempts: state.failedAttempts + 1,
      };
    case "SUBMIT_BLOCKED":
      return {
        ...state,
        submitting: false,
        phase: "invalid",
        error: "La invitación fue bloqueada por demasiados intentos incorrectos.",
      };
    case "MAGIC_LINK_SENT":
      return { ...state, submitting: false, phase: "magic-link-sent" };
    case "ACCEPTED":
      return { ...state, submitting: false, phase: "accepted" };
    case "CLEAR_ERROR":
      return { ...state, error: "" };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function errorMessageForCode(err: InvitationError): string {
  switch (err.code) {
    case "INVALID_CODE":
      return "Código inválido. Revisá el email de invitación y reintentá.";
    case "BLOCKED":
      return "La invitación fue bloqueada por demasiados intentos incorrectos.";
    case "ALREADY_MEMBER":
      return "Ya sos miembro de esta organización.";
    case "ACCOUNT_EXISTS":
      return "Ya existe una cuenta con este email. Usá el enlace de acceso.";
    case "INVALID_INPUT":
      return "Datos inválidos. Revisá el formulario y reintentá.";
    default:
      return err.message || "Error inesperado. Reintentá más tarde.";
  }
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export const AcceptInvitationPage: React.FC = () => {
  const { invitationId } = useParams<{ invitationId: string }>();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, undefined, init);

  // --- Preview al montar ---
  useEffect(() => {
    if (!invitationId) {
      dispatch({ type: "PREVIEW_FAIL" });
      return;
    }
    let cancelled = false;
    previewInvitation(invitationId)
      .then((preview) => {
        if (!cancelled) dispatch({ type: "PREVIEW_OK", preview });
      })
      .catch(() => {
        if (!cancelled) dispatch({ type: "PREVIEW_FAIL" });
      });
    return () => {
      cancelled = true;
    };
  }, [invitationId]);

  // --- Submit signup ---
  const handleSignup = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!invitationId) return;

      // Validación client-side antes del fetch
      if (state.name.trim().length === 0 || state.name.trim().length > 120) {
        dispatch({ type: "SUBMIT_ERROR", error: "El nombre es requerido (máx. 120 caracteres)." });
        return;
      }
      if (state.password.length < 8) {
        dispatch({
          type: "SUBMIT_ERROR",
          error: "La contraseña debe tener al menos 8 caracteres.",
        });
        return;
      }
      if (state.acceptCode.length !== 10) {
        dispatch({
          type: "SUBMIT_ERROR",
          error: "El código de aceptación debe tener 10 caracteres.",
        });
        return;
      }

      dispatch({ type: "SUBMIT_START" });
      try {
        const result = await acceptSignup({
          invitationId,
          acceptCode: state.acceptCode,
          name: state.name.trim(),
          password: state.password,
        });
        dispatch({ type: "ACCEPTED" });
        navigate(result.redirectTo || "/", { replace: true });
      } catch (err) {
        if (err instanceof InvitationError) {
          if (err.code === "BLOCKED") {
            dispatch({ type: "SUBMIT_BLOCKED" });
          } else {
            dispatch({ type: "SUBMIT_ERROR", error: errorMessageForCode(err) });
          }
        } else {
          dispatch({ type: "SUBMIT_ERROR", error: "Error de conexión. Probá de nuevo." });
        }
      }
    },
    [invitationId, state.name, state.password, state.acceptCode, navigate],
  );

  // --- Submit login-and-accept ---
  const handleLoginAndAccept = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!invitationId) return;

      if (state.acceptCode.length !== 10) {
        dispatch({
          type: "SUBMIT_ERROR",
          error: "El código de aceptación debe tener 10 caracteres.",
        });
        return;
      }

      dispatch({ type: "SUBMIT_START" });
      try {
        await loginAndAccept({ invitationId, acceptCode: state.acceptCode });
        dispatch({ type: "MAGIC_LINK_SENT" });
      } catch (err) {
        if (err instanceof InvitationError) {
          if (err.code === "BLOCKED") {
            dispatch({ type: "SUBMIT_BLOCKED" });
          } else {
            dispatch({ type: "SUBMIT_ERROR", error: errorMessageForCode(err) });
          }
        } else {
          dispatch({ type: "SUBMIT_ERROR", error: "Error de conexión. Probá de nuevo." });
        }
      }
    },
    [invitationId, state.acceptCode],
  );

  // --- Submit accept-now ---
  const handleAcceptNow = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!invitationId) return;

      if (state.acceptCode.length !== 10) {
        dispatch({
          type: "SUBMIT_ERROR",
          error: "El código de aceptación debe tener 10 caracteres.",
        });
        return;
      }

      dispatch({ type: "SUBMIT_START" });
      try {
        await acceptNow({ invitationId, acceptCode: state.acceptCode });
        dispatch({ type: "ACCEPTED" });
        navigate("/", { replace: true });
      } catch (err) {
        if (err instanceof InvitationError) {
          if (err.code === "BLOCKED") {
            dispatch({ type: "SUBMIT_BLOCKED" });
          } else {
            dispatch({ type: "SUBMIT_ERROR", error: errorMessageForCode(err) });
          }
        } else {
          dispatch({ type: "SUBMIT_ERROR", error: "Error de conexión. Probá de nuevo." });
        }
      }
    },
    [invitationId, state.acceptCode, navigate],
  );

  // --- Logout y volver a cargar ---
  const handleLogoutAndRetry = useCallback(async () => {
    await signOut();
    window.location.reload();
  }, []);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const Header: React.FC = () => (
    <>
      <span className="inv-logo">◈ Tagliapietra Portal</span>
      {state.orgName && (
        <div className="inv-org">
          <span className="inv-org-label">Invitación a</span>
          <strong className="inv-org-name">{state.orgName}</strong>
          {state.inviterName && (
            <span className="inv-inviter">enviada por {state.inviterName}</span>
          )}
        </div>
      )}
    </>
  );

  const ErrorMessage: React.FC = () =>
    state.error ? <div className="inv-msg error">{state.error}</div> : null;

  const CodeField: React.FC = () => (
    <div className="form-group">
      <label htmlFor="acceptCode">Código de aceptación</label>
      <input
        id="acceptCode"
        type="text"
        value={state.acceptCode}
        onChange={(e) =>
          dispatch({ type: "SET_FIELD", field: "acceptCode", value: e.target.value })
        }
        maxLength={10}
        autoComplete="one-time-code"
        placeholder="10 caracteres"
        required
      />
      <p className="field-hint">Encontrás el código en el email de invitación.</p>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Vistas por fase
  // ---------------------------------------------------------------------------

  if (state.phase === "loading") {
    return (
      <div className="inv-container">
        <div className="inv-card">
          <span className="inv-logo">◈ Tagliapietra Portal</span>
          <div className="inv-loading">
            <div className="spinner" />
            <p>Cargando invitación…</p>
          </div>
        </div>
        <InvStyles />
      </div>
    );
  }

  if (state.phase === "invalid") {
    return (
      <div className="inv-container">
        <div className="inv-card">
          <span className="inv-logo">◈ Tagliapietra Portal</span>
          <h2>Invitación inválida</h2>
          <div className="inv-msg error">
            Esta invitación no es válida, ya fue usada o expiró. Pedí una nueva invitación al
            administrador de tu organización.
          </div>
          {state.error && <div className="inv-msg error">{state.error}</div>}
        </div>
        <InvStyles />
      </div>
    );
  }

  if (state.phase === "magic-link-sent") {
    return (
      <div className="inv-container">
        <div className="inv-card">
          <Header />
          <h2>Revisá tu inbox</h2>
          <div className="inv-msg notice">
            Te enviamos un enlace de acceso a tu email. Hacé clic en el enlace para ingresar y
            aceptar la invitación automáticamente.
          </div>
          <p className="inv-hint">Si no lo ves en unos minutos, revisá la carpeta de spam.</p>
        </div>
        <InvStyles />
      </div>
    );
  }

  if (state.phase === "accepted") {
    return (
      <div className="inv-container">
        <div className="inv-card">
          <Header />
          <h2>Invitación aceptada</h2>
          <div className="inv-msg notice">
            Bienvenido/a{state.orgName ? ` a ${state.orgName}` : ""}. Redirigiendo…
          </div>
        </div>
        <InvStyles />
      </div>
    );
  }

  if (state.phase === "wrong-account") {
    return (
      <div className="inv-container">
        <div className="inv-card">
          <Header />
          <h2>Cuenta equivocada</h2>
          <div className="inv-msg error">
            Estás logueado como <strong>{state.currentEmail}</strong> pero la invitación es para
            otra cuenta. Cerrá sesión y volvé a intentar.
          </div>
          <button
            type="button"
            className="inv-submit"
            onClick={handleLogoutAndRetry}
            disabled={state.submitting}
          >
            Cerrar sesión y reintentar
          </button>
        </div>
        <InvStyles />
      </div>
    );
  }

  if (state.phase === "signup-required") {
    return (
      <div className="inv-container">
        <form className="inv-card" onSubmit={handleSignup} noValidate>
          <Header />
          <h2>Crear cuenta</h2>
          <p className="inv-sub">Completá los datos para aceptar la invitación.</p>
          <ErrorMessage />

          <div className="form-group">
            <label htmlFor="name">Nombre completo</label>
            <input
              id="name"
              type="text"
              value={state.name}
              onChange={(e) =>
                dispatch({ type: "SET_FIELD", field: "name", value: e.target.value })
              }
              maxLength={120}
              autoComplete="name"
              placeholder="Tu nombre"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <input
              id="password"
              type="password"
              value={state.password}
              onChange={(e) =>
                dispatch({ type: "SET_FIELD", field: "password", value: e.target.value })
              }
              autoComplete="new-password"
              minLength={8}
              placeholder="Mínimo 8 caracteres"
              required
            />
          </div>

          <CodeField />

          <button type="submit" className="inv-submit" disabled={state.submitting}>
            {state.submitting ? "Creando cuenta…" : "Crear cuenta y aceptar"}
          </button>
        </form>
        <InvStyles />
      </div>
    );
  }

  if (state.phase === "login-required") {
    return (
      <div className="inv-container">
        <form className="inv-card" onSubmit={handleLoginAndAccept} noValidate>
          <Header />
          <h2>Aceptar invitación</h2>
          <p className="inv-sub">Ingresá el código de tu email para recibir el enlace de acceso.</p>
          <ErrorMessage />

          <CodeField />

          <button type="submit" className="inv-submit" disabled={state.submitting}>
            {state.submitting ? "Enviando…" : "Enviar enlace de acceso"}
          </button>
        </form>
        <InvStyles />
      </div>
    );
  }

  // phase === "ready"
  return (
    <div className="inv-container">
      <form className="inv-card" onSubmit={handleAcceptNow} noValidate>
        <Header />
        <h2>Aceptar invitación</h2>
        <p className="inv-sub">Confirmá el código del email para unirte.</p>
        <ErrorMessage />

        <CodeField />

        <button type="submit" className="inv-submit" disabled={state.submitting}>
          {state.submitting ? "Confirmando…" : "Confirmar y unirme"}
        </button>
      </form>
      <InvStyles />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Estilos — misma paleta y tokens que LoginForm
// ---------------------------------------------------------------------------

const InvStyles: React.FC = () => (
  <style>{`
    .inv-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      background: var(--color-bg);
      font-family: var(--font-sans);
      padding: 24px 16px;
      box-sizing: border-box;
    }
    .inv-card {
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      padding: 32px;
      border-radius: 10px;
      width: 100%;
      max-width: 420px;
      box-sizing: border-box;
    }
    .inv-logo {
      font-family: var(--font-mono);
      font-size: 13px;
      font-weight: 600;
      letter-spacing: .15em;
      color: var(--color-accent);
      display: block;
      margin-bottom: 16px;
    }
    .inv-org {
      display: flex;
      flex-direction: column;
      gap: 2px;
      margin-bottom: 16px;
      padding: 10px 12px;
      background: var(--color-surface-2);
      border: 1px solid var(--color-border);
      border-radius: 6px;
    }
    .inv-org-label {
      font-size: 11px;
      color: var(--color-muted);
      text-transform: uppercase;
      letter-spacing: .08em;
    }
    .inv-org-name {
      font-size: 15px;
      color: var(--color-text);
      font-weight: 600;
    }
    .inv-inviter {
      font-size: 12px;
      color: var(--color-muted);
    }
    .inv-card h2 {
      margin: 0 0 4px;
      font-size: 18px;
      color: var(--color-text);
    }
    .inv-sub {
      color: var(--color-muted);
      font-size: 12px;
      margin: 0 0 20px;
    }
    .inv-hint {
      color: var(--color-muted);
      font-size: 12px;
      margin: 12px 0 0;
    }
    .inv-loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 24px 0;
      color: var(--color-muted);
      font-size: 13px;
    }
    .inv-msg {
      padding: 10px 12px;
      border-radius: 6px;
      margin-bottom: 16px;
      font-size: 13px;
      line-height: 1.5;
    }
    .inv-msg.error {
      background: rgba(248, 113, 113, .12);
      color: var(--color-danger);
      border: 1px solid rgba(248, 113, 113, .3);
    }
    .inv-msg.notice {
      background: rgba(52, 211, 153, .12);
      color: var(--color-success);
      border: 1px solid rgba(52, 211, 153, .3);
    }
    .form-group { margin-bottom: 16px; }
    .form-group label {
      display: block;
      margin-bottom: 6px;
      color: var(--color-muted);
      font-size: 12px;
    }
    .form-group input {
      width: 100%;
      padding: 10px 12px;
      background: var(--color-surface-2);
      border: 1px solid var(--color-border);
      border-radius: 6px;
      color: var(--color-text);
      font-size: 14px;
      box-sizing: border-box;
    }
    .form-group input:focus {
      outline: none;
      border-color: var(--color-accent);
    }
    .field-hint {
      margin: 4px 0 0;
      font-size: 11px;
      color: var(--color-muted);
    }
    .inv-submit {
      width: 100%;
      padding: 10px;
      margin-top: 4px;
      background: var(--color-accent);
      color: #0b1220;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
    }
    .inv-submit:disabled { opacity: .5; cursor: default; }
  `}</style>
);
